import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import NotFound from "@/pages/NotFound";

import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Quick Credit | Personal Loans $1,000–$50,000 from 5.96% APR" },
      {
        name: "description",
        content:
          "Compare personal loans from $1,000 to $50,000 over 12–60 months. Fixed APRs from 5.96% to 35.99%. Check your rate with no impact to your credit score.",
      },
      { name: "author", content: "Quick Credit" },
      { property: "og:title", content: "Quick Credit | Personal Loans from 5.96% APR" },
      {
        property: "og:description",
        content:
          "Transparent personal loans from $1,000 to $50,000. Clear rates, no hidden fees, responsible lending.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://quickcredit.live/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@QuickCredit" },
      { name: "twitter:title", content: "Quick Credit | Personal Loans from 5.96% APR" },
      {
        name: "twitter:description",
        content:
          "Transparent personal loans from $1,000 to $50,000. Clear rates, no hidden fees, responsible lending.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "canonical", href: "https://quickcredit.live/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FinancialService",
          name: "Quick Credit",
          url: "https://quickcredit.live/",
          description:
            "Personal loans from $1,000 to $50,000 with fixed APRs from 5.96% to 35.99%.",
          telephone: "+1-917-983-2016",
          email: "support@quickcredit.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "1180 Avenue of the Americas, Suite 800",
            addressLocality: "New York",
            addressRegion: "NY",
            postalCode: "10036",
            addressCountry: "US",
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:00",
              closes: "20:00",
            },
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: "Saturday",
              opens: "09:00",
              closes: "17:00",
            },
          ],
          areaServed: "US",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Outlet />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold text-foreground">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong while loading this page. You can try again or head back home.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-md border border-border text-foreground font-medium hover:bg-muted transition-colors"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
